/**
 * SHIFT-SCHEDULE-001 / SHIFT-ATTENDANCE-001 — Shift & Attendance section
 * (Edit Employee → Employment). One place for shift definition + attendance
 * register code, weekly off and (unified) attendance timezone. Storage behind
 * the API is unchanged (employee_shift_history + users.* + attendance snapshots).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Clock, Loader2, MoonStar, Save, History, RefreshCw, Globe2, CalendarOff, Link2Off, Lock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const SHIFT_OPTIONS = [
  { value: 'regular', label: 'Regular / Day Shift' },
  { value: 'morning', label: 'Morning Shift' },
  { value: 'night', label: 'Night Shift' },
  { value: 'custom', label: 'Custom Shift' },
];
const TYPE_TO_CODE = { regular: 'DS', morning: 'MS', night: 'NS' };
const CODE_OPTIONS = [
  { value: 'DS', label: 'DS — Day Shift' },
  { value: 'MS', label: 'MS — Morning Shift' },
  { value: 'NS', label: 'NS — Night Shift' },
];
const TZ_OPTIONS = [
  { value: '', label: 'Inherit (team / company default)' },
  { value: 'Asia/Kolkata', label: 'India — Asia/Kolkata (IST)' },
  { value: 'America/New_York', label: 'US Eastern — America/New_York (ET)' },
  { value: 'America/Chicago', label: 'US Central — America/Chicago (CT)' },
  { value: 'America/Denver', label: 'US Mountain — America/Denver (MT)' },
  { value: 'America/Los_Angeles', label: 'US Pacific — America/Los_Angeles (PT)' },
  { value: 'UTC', label: 'UTC' },
];
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const dayNames = (arr) => (arr && arr.length ? arr.map((d) => DAYS[d]).join(', ') : null);
const EMPTY_FORM = () => ({ shift_type: '', start_time: '', end_time: '', effective_from: todayStr(), register_code: '', attendance_timezone: '', weekly_off_days: [] });

const todayStr = () => new Date().toISOString().slice(0, 10);

// Mirrors backend _shift_at(): latest row with effective_from <= date (history is desc from API)
const shiftInForce = (history, dateStr) => {
  const asc = [...(history || [])].sort((a, b) =>
    a.effective_from.localeCompare(b.effective_from) || (a.created_at || '').localeCompare(b.created_at || ''));
  let chosen = null;
  for (const s of asc) { if (s.effective_from <= dateStr) chosen = s; else break; }
  return chosen;
};

export default function ShiftScheduleSection({ employeeId, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState(EMPTY_FORM);
  const [tzCtx, setTzCtx] = useState(null); // SHIFT-DST-001: abbreviation/offset for the selected effective date

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hr/employees/${employeeId}/shift-attendance`, { headers: authHeaders() });
      setData(r.data);
      const a = r.data?.attendance || {};
      const c = r.data?.current;
      // Prefill from what is in force so WO / timezone / code can be changed without re-typing the shift
      setForm((p) => ({ ...p, shift_type: c?.shift_type || '', start_time: c?.start_time || '', end_time: c?.end_time || '',
        register_code: a.register_code || '', attendance_timezone: a.attendance_timezone || '', weekly_off_days: a.weekly_off_days || [] }));
    } catch (e) {
      console.error('shift load failed', e);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!form.effective_from || !/^\d{4}-\d{2}-\d{2}$/.test(form.effective_from)) return;
    let live = true;
    axios.get(`${API}/api/hr/employees/${employeeId}/shift-attendance`, { params: { date: form.effective_from }, headers: authHeaders() })
      .then((r) => { if (live) setTzCtx(r.data?.timezone_context || null); }).catch(() => {});
    return () => { live = false; };
  }, [employeeId, form.effective_from, data?.attendance?.resolved_attendance_timezone]);

  const setField = (k, v) => {
    setForm((p) => {
      const next = { ...p, [k]: v };
      if (k === 'shift_type' && TYPE_TO_CODE[v]) next.register_code = TYPE_TO_CODE[v];
      return next;
    });
    setErrors((p) => ({ ...p, [k]: undefined }));
  };
  const toggleDay = (d) => setField('weekly_off_days', form.weekly_off_days.includes(d) ? form.weekly_off_days.filter((x) => x !== d) : [...form.weekly_off_days, d].sort());
  const att = data?.attendance || {};
  const linked = !!att.linked;
  const canWrite = data?.can_write !== false;
  const sameList = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);
  const attendanceDirty = linked && ((form.register_code || '') !== (att.register_code || '')
    || (form.attendance_timezone || '') !== (att.attendance_timezone || '') || !sameList(form.weekly_off_days, att.weekly_off_days));

  const crossesMidnight = form.start_time && form.end_time && form.end_time <= form.start_time;

  const formComplete = form.shift_type && form.start_time && form.end_time && form.effective_from;
  const inForce = formComplete ? shiftInForce(data?.history, form.effective_from) : null;
  const unchanged = !!(inForce && inForce.shift_type === form.shift_type
    && inForce.start_time === form.start_time && inForce.end_time === form.end_time) && !attendanceDirty;
  const existingSameDate = (data?.history || []).some((s) => s.effective_from === form.effective_from);
  const willReplace = !!(formComplete && existingSameDate && !unchanged);

  const validate = () => {
    const errs = {};
    if (!form.shift_type) errs.shift_type = 'Shift Type is required';
    if (!form.start_time) errs.start_time = 'Shift Start Time is required';
    if (!form.end_time) errs.end_time = 'Shift End Time is required';
    if (form.start_time && form.end_time && form.start_time === form.end_time) errs.end_time = 'End Time cannot equal Start Time';
    if (!form.effective_from) errs.effective_from = 'Effective From is required';
    if (form.shift_type === 'custom' && linked && !form.register_code) errs.register_code = 'Register Code is required for a Custom shift';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSaveClick = () => {
    if (!validate()) return;
    if (willReplace) { setConfirmOpen(true); return; }
    save();
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { ...form, weekly_off_days: form.weekly_off_days?.length ? form.weekly_off_days : null, attendance_timezone: form.attendance_timezone || null };
      const r = await axios.put(`${API}/api/hr/employees/${employeeId}/shift-attendance`, payload, { headers: authHeaders() });
      if (r.data?.unchanged && !r.data?.attendance_changed) {
        toast.info('No changes — this configuration is already in force for the selected date');
      } else {
        toast.success(r.data?.replaced ? `Entry effective ${form.effective_from} replaced` : 'Shift & attendance saved');
        if (r.data?.announcement_id) toast.success('Shift-change announcement published to all active users', { description: 'Each user must acknowledge it.' });
        if (r.data?.linked_user_warning) toast.info(r.data.linked_user_warning);
        setForm(EMPTY_FORM());
        await load();
        // keep the Employee Status card (resolved timezone etc.) in sync with what was just saved
        if (onSaved) await onSaved();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save shift schedule');
    } finally {
      setSaving(false);
    }
  };

  const ShiftBadge = ({ s }) => (
    <span className="inline-flex items-center gap-1.5">
      {s.shift_type === 'night' ? <MoonStar className="w-3.5 h-3.5 text-indigo-500" /> : <Clock className="w-3.5 h-3.5 text-slate-400" />}
      <span className="font-semibold text-slate-800">{s.shift_type_label}</span>
      <span className="font-mono text-slate-600">{s.start_time_display} – {s.end_time_display}</span>
    </span>
  );

  return (
    <div className="mt-8 border-t border-slate-100 pt-6" data-testid="shift-schedule-section">
      <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-1">
        <Clock className="w-4 h-4" /> Shift &amp; Attendance
        {!canWrite && <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5" data-testid="shift-attendance-readonly"><Lock className="w-3 h-3" /> Read-only</span>}
      </h4>
      <p className="text-xs text-slate-400 mb-4">
        Expected working schedule and attendance-register settings (register code, weekly off, timezone). Actual attendance always comes from real session records.
        Changes are effective-dated; historical dates keep the configuration that was in force.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-4"><Loader2 className="w-4 h-4 animate-spin" /> Loading shift…</div>
      ) : (
        <>
          {/* Current shift */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 mb-4 text-sm" data-testid="current-shift-card">
            <span className="text-xs text-slate-400 uppercase tracking-wider mr-3">Current</span>
            {data?.current ? (
              <>
                <ShiftBadge s={data.current} />
                <span className="ml-2 text-xs text-slate-400">effective {data.current.effective_from}</span>
              </>
            ) : (
              <span className="text-slate-400" data-testid="no-shift-configured">No shift configured</span>
            )}
            {linked && (
              <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600" data-testid="current-attendance-summary">
                <span>Register <b className="font-mono">{att.register_code || '—'}</b></span>
                <span className="inline-flex items-center gap-1"><CalendarOff className="w-3 h-3 text-slate-400" /> WO {dayNames(att.weekly_off_days) || `${dayNames(att.team_weekly_off_days) || dayNames(att.default_weekly_off_days)} (${att.weekly_off_source})`}</span>
                <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3 text-slate-400" /> {att.resolved_attendance_timezone} ({att.attendance_timezone_source})</span>
              </div>
            )}
            {!linked && (
              <div className="mt-1.5 flex items-center gap-1.5 text-xs text-amber-700" data-testid="attendance-unlinked-hint">
                <Link2Off className="w-3.5 h-3.5" /> Not linked to a user account — link one via Quick Actions to manage register code, weekly off and timezone.
              </div>
            )}
            {(data?.upcoming || []).map((s) => (
              <div key={s.id} className="mt-1.5 text-xs text-amber-700" data-testid="upcoming-shift-row">
                Upcoming: <span className="font-semibold">{s.shift_type_label}</span> {s.start_time_display} – {s.end_time_display} from {s.effective_from}
              </div>
            ))}
          </div>

          {/* Assign / change shift */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Shift Type<span className="text-red-500 ml-0.5">*</span></label>
              <Select value={form.shift_type ?? ''} onValueChange={(v) => setField('shift_type', v)}>
                <SelectTrigger data-testid="shift-type-select" className={errors.shift_type ? 'border-red-300' : ''}>
                  <SelectValue placeholder="Select shift type" />
                </SelectTrigger>
                <SelectContent>
                  {SHIFT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {errors.shift_type && <p className="text-xs text-red-600 mt-1" data-testid="shift-type-error">{errors.shift_type}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Effective From Date<span className="text-red-500 ml-0.5">*</span></label>
              <Input type="date" min={todayStr()} value={form.effective_from} data-testid="shift-effective-from"
                onChange={(e) => setField('effective_from', e.target.value)} className={errors.effective_from ? 'border-red-300' : ''} />
              {errors.effective_from && <p className="text-xs text-red-600 mt-1">{errors.effective_from}</p>}
            {tzCtx && (
                <p className="text-[11px] text-slate-500 mt-1 inline-flex flex-wrap items-center gap-1" data-testid="shift-tz-context">
                  <Globe2 className="w-3 h-3 text-slate-400" />
                  <span className="font-semibold text-slate-700" data-testid="shift-tz-abbr">{tzCtx.abbreviation}</span>
                  <span data-testid="shift-tz-offset">({tzCtx.utc_offset})</span>
                  <span>on {form.effective_from} · {tzCtx.timezone}</span>
                  {tzCtx.observes_dst && tzCtx.next_transition && (
                    <span className="text-amber-700" data-testid="shift-tz-next-transition">· switches to {tzCtx.next_transition.abbreviation} ({tzCtx.next_transition.utc_offset}) on {tzCtx.next_transition.date} — local shift hours stay unchanged</span>
                  )}
                </p>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Shift Start Time<span className="text-red-500 ml-0.5">*</span></label>
              <Input type="time" value={form.start_time} data-testid="shift-start-time"
                onChange={(e) => setField('start_time', e.target.value)} className={errors.start_time ? 'border-red-300' : ''} />
              {errors.start_time && <p className="text-xs text-red-600 mt-1" data-testid="shift-start-error">{errors.start_time}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Shift End Time<span className="text-red-500 ml-0.5">*</span></label>
              <Input type="time" value={form.end_time} data-testid="shift-end-time"
                onChange={(e) => setField('end_time', e.target.value)} className={errors.end_time ? 'border-red-300' : ''} />
              {errors.end_time && <p className="text-xs text-red-600 mt-1" data-testid="shift-end-error">{errors.end_time}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Register Code{form.shift_type === 'custom' && <span className="text-red-500 ml-0.5">*</span>}</label>
              <Select value={form.register_code || ''} onValueChange={(v) => setField('register_code', v)} disabled={!linked || !canWrite}>
                <SelectTrigger data-testid="register-code-select" className={errors.register_code ? 'border-red-300' : ''}>
                  <SelectValue placeholder={linked ? 'Derived from shift type' : 'Link a user account first'} />
                </SelectTrigger>
                <SelectContent>{CODE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Printed on the Attendance Register (P-DS / P-MS / P-NS). Regular→DS, Morning→MS, Night→NS; Custom needs a pick.</p>
              {errors.register_code && <p className="text-xs text-red-600 mt-1" data-testid="register-code-error">{errors.register_code}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Attendance Timezone</label>
              <Select value={form.attendance_timezone || '__inherit'} onValueChange={(v) => setField('attendance_timezone', v === '__inherit' ? '' : v)} disabled={!linked || !canWrite}>
                <SelectTrigger data-testid="attendance-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TZ_OPTIONS.map((o) => <SelectItem key={o.value || '__inherit'} value={o.value || '__inherit'}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              {linked && <p className="text-[11px] text-slate-400 mt-1">Inherits {att.team_attendance_timezone ? `team ${att.team_name}: ${att.team_attendance_timezone}` : `company default: ${att.company_default_timezone}`} when blank. Raw timestamps stay UTC.</p>}
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Weekly Off <span className="text-slate-400 font-normal">(employee override)</span></label>
              <div className="flex flex-wrap gap-1.5" data-testid="weekly-off-days">
                {DAYS.map((d, i) => (
                  <button key={d} type="button" disabled={!linked || !canWrite} onClick={() => toggleDay(i)} data-testid={`weekly-off-${d.toLowerCase()}`}
                    className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${form.weekly_off_days.includes(i) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300'} disabled:opacity-50`}>
                    {d}
                  </button>
                ))}
              </div>
              {linked && <p className="text-[11px] text-slate-400 mt-1">Clear all to inherit {att.team_weekly_off_days ? `team ${att.team_name}: ${dayNames(att.team_weekly_off_days)}` : `default ${dayNames(att.default_weekly_off_days)}`}.</p>}
            </div>
          </div>
          {crossesMidnight && (
            <p className="text-xs text-indigo-600 mt-2 flex items-center gap-1.5" data-testid="cross-midnight-hint">
              <MoonStar className="w-3.5 h-3.5" /> This shift ends the next day (crosses midnight) — it will be treated as one shift.
            </p>
          )}
          <div className="flex items-center justify-end gap-3 mt-3">
            {unchanged && (
              <span className="text-xs text-slate-400" data-testid="shift-unchanged-hint">No changes to save — this shift is already in force for {form.effective_from}</span>
            )}
            {willReplace && (
              <span className="text-xs text-amber-700" data-testid="shift-replace-hint">An entry already exists for {form.effective_from} — saving will replace it</span>
            )}
            <Button size="sm" onClick={onSaveClick} disabled={saving || unchanged || !canWrite} className="gap-2" data-testid="save-shift-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : willReplace ? <RefreshCw className="w-4 h-4" /> : <Save className="w-4 h-4" />}
              {willReplace ? `Replace entry for ${form.effective_from}` : 'Save Shift & Attendance'}
            </Button>
          </div>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent data-testid="shift-replace-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Replace shift entry for {form.effective_from}?</AlertDialogTitle>
                <AlertDialogDescription>
                  A shift schedule already exists with this Effective From date. The existing entry will be updated in place —
                  no additional Shift History row will be created. Historical dates are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="shift-replace-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="shift-replace-confirm-btn" onClick={() => { setConfirmOpen(false); save(); }}>
                  Replace entry
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Attendance change history */}
          {(att.history || []).length > 0 && (
            <div className="mt-5" data-testid="attendance-history-list">
              <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <History className="w-3.5 h-3.5" /> Attendance Config History
              </h5>
              <div className="space-y-1.5">
                {att.history.map((h) => (
                  <div key={h.id} className="flex items-center gap-3 text-xs text-slate-600 border-b border-slate-50 pb-1.5">
                    <Badge variant="outline" className="font-mono shrink-0">{h.shift || '—'}</Badge>
                    <span>WO {dayNames(h.weekly_off_days) || 'inherit'}</span>
                    <span>TZ {h.attendance_timezone || 'inherit'}</span>
                    <span className="text-slate-400">effective {h.effective_from}</span>
                    <span className="text-slate-300 ml-auto">by {h.set_by_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* History */}
          {(data?.history || []).length > 0 && (
            <div className="mt-5" data-testid="shift-history-list">
              <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <History className="w-3.5 h-3.5" /> Shift History
              </h5>
              <div className="space-y-1.5">
                {data.history.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 text-xs text-slate-600 border-b border-slate-50 pb-1.5">
                    <Badge variant="outline" className="capitalize shrink-0">{s.shift_type}</Badge>
                    <span className="font-mono">{s.start_time_display} – {s.end_time_display}</span>
                    <span className="text-slate-400">effective {s.effective_from}</span>
                    {s.updated_at && <span className="text-amber-600" data-testid="shift-history-updated">updated {s.updated_at.slice(0, 10)}</span>}
                    <span className="text-slate-300 ml-auto">by {s.set_by_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
